"use client";

import { useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "./../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "./../../Components/GroupedContainer";
import axios from "axios";
import Swal from "sweetalert2";
import { ArrowLeft, Save, Upload, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  TbUser,
  TbFilter,
  TbCurrencyDollar,
  TbClipboardText,
  TbCalendar,
  TbPaperclip,
  TbChevronDown,
  TbX,
  TbRefresh,
} from "react-icons/tb";

function NewDriverExpense() {
  const navigate = useNavigate();
  const API_PREFIX = process.env.REACT_APP_API_PREFIX;

  const api = useMemo(() => {
    const baseURL = (API_PREFIX || "").replace(/\/+$/, "");
    const instance = axios.create({ baseURL });

    instance.interceptors.request.use((config) => {
      const userToken = localStorage.getItem("token");
      config.headers = config.headers || {};
      config.headers["Screen-Name"] = "/Load";
      if (userToken) config.headers.Authorization = `Bearer ${userToken}`;
      return config;
    });

    return instance;
  }, [API_PREFIX]);

  const [drivers, setDrivers] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState(null);

  // Form fields
  const [driverId, setDriverId] = useState("");
  const [category, setCategory] = useState("Fuel");
  const [paidBy, setPaidBy] = useState(""); // ✅ NEW (nullable)
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [expenseAt, setExpenseAt] = useState("");

  // ✅ Storage Fee-only fields (required only for Storage Fee)
  const [orderId, setOrderId] = useState("");
  const [buyerNumber, setBuyerNumber] = useState("");
  const [lotNumber, setLotNumber] = useState("");

  const isStorageFee = category === "Storage Fee";

  // attachments on create
  const [documents, setDocuments] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState("");

  // after create: enable attachment CRUD
  const [createdExpense, setCreatedExpense] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingMore, setUploadingMore] = useState(false);

  const unwrapApiData = (res) => res?.data?.data ?? res?.data;

  const normalizeExpenseAt = (dtLocal) => {
    if (!dtLocal) return "";
    if (dtLocal.includes("T") && dtLocal.length === 16) {
      return dtLocal.replace("T", " ") + ":00";
    }
    return dtLocal.replace("T", " ");
  };

  const extractErrorPayload = (err) => err?.response?.data || { message: "Unexpected error" };

  const formatMoneyString = (value) => {
    if (value === null || value === undefined) return "";
    const raw = String(value).trim();
    if (raw === "") return "";
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return "";
    return parsed.toFixed(2);
  };

  const handleAmountBlur = () => {
    setAmount(formatMoneyString(amount));
  };

  const clearField = (setter) => (e) => {
    e?.preventDefault?.();
    setter("");
  };

  const FieldError = ({ name }) => {
    if (!errors) return null;
    const msg = errors?.[name];
    if (!msg) return null;
    const text = Array.isArray(msg) ? msg.join(" ") : String(msg);
    return <div className="text-red-400 text-xs mt-2">{text}</div>;
  };

  useEffect(() => {
    const fetchDrivers = async () => {
      setLoadingRefs(true);
      try {
        // EXATAMENTE o endpoint solicitado
        const res = await api.get("https://new.cajuu.app/api/driver");
        const data = unwrapApiData(res);
        const list = Array.isArray(data) ? data : data?.data;
        setDrivers(Array.isArray(list) ? list : []);
      } catch (e) {
        setDrivers([]);
      } finally {
        setLoadingRefs(false);
      }
    };

    fetchDrivers();
  }, [api]);

  // ✅ If category changes away from Storage Fee, clear Storage Fee-only fields and related errors
  useEffect(() => {
    if (!isStorageFee) {
      setOrderId("");
      setBuyerNumber("");
      setLotNumber("");

      setErrors((prev) => {
        if (!prev || typeof prev !== "object") return prev;
        const next = { ...prev };
        delete next.order_id;
        delete next.buyer_number;
        delete next.lot_number;
        return Object.keys(next).length ? next : null;
      });
    }
  }, [isStorageFee]);

  const validateClientSide = () => {
    const errs = {};

    if (!category) errs.category = ["Category is required."];
    if (amount === "" || Number(amount) < 0) errs.amount = ["Amount is required and must be >= 0."];
    if (!expenseAt) errs.expense_at = ["Expense date/time is required."];

    if (category === "Other" && !String(description || "").trim()) {
      errs.description = ["Description is required when category is Other."];
    }

    // ✅ Storage Fee requires order_id, buyer_number, lot_number
    if (isStorageFee) {
      if (!String(orderId || "").trim()) errs.order_id = ["Order ID is required for Storage Fee."];
      if (!String(buyerNumber || "").trim()) errs.buyer_number = ["Buyer Number is required for Storage Fee."];
      if (!String(lotNumber || "").trim()) errs.lot_number = ["Lot Number is required for Storage Fee."];
    }

    // paid_by é nullable (sem validação obrigatória)

    setErrors(Object.keys(errs).length ? errs : null);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors(null);

    if (!validateClientSide()) {
      Swal.fire({
        icon: "error",
        title: "Validation error",
        text: "Please fix the highlighted fields.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const fd = new FormData();

      if (driverId) fd.append("driver_id", String(driverId));
      fd.append("category", category);

      // ✅ Storage Fee-only fields: only append when Storage Fee
      if (isStorageFee) {
        fd.append("order_id", String(orderId).trim());
        fd.append("buyer_number", String(buyerNumber).trim());
        fd.append("lot_number", String(lotNumber).trim());
      }

      // ✅ NEW: paid_by nullable (só envia se tiver valor)
      if (paidBy) fd.append("paid_by", paidBy);

      // dinheiro com 2 casas (string -> number no backend)
      fd.append("amount", formatMoneyString(amount));

      if (description !== "") fd.append("description", description);
      fd.append("expense_at", normalizeExpenseAt(expenseAt));

      if (attachmentDescription) fd.append("attachment_description", attachmentDescription);

      if (documents && documents.length > 0) {
        documents.forEach((f) => {
          fd.append("documents[]", f);
        });
      }

      const res = await api.post("/driverexpense", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const created = unwrapApiData(res);
      setCreatedExpense(created);

      Swal.fire({
        icon: "success",
        title: "Expense created",
        text: `Driver Expense #${created?.id ?? ""} created successfully.`,
      });

      const initialAttachments = created?.attachments;
      if (Array.isArray(initialAttachments)) {
        setAttachments(initialAttachments);
      } else if (created?.id) {
        await refreshAttachments(created.id);
      }

      // navigate("/expense");
    } catch (err) {
      const payload = extractErrorPayload(err);

      const serverErrors =
        payload?.message && typeof payload.message === "object"
          ? payload.message
          : payload?.data && typeof payload.data === "object"
          ? payload.data
          : null;

      if (serverErrors) setErrors(serverErrors);

      Swal.fire({
        icon: "error",
        title: "Failed to create",
        text:
          payload?.data?.toString?.() ||
          payload?.message?.toString?.() ||
          "Unexpected error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const refreshAttachments = async (expenseId) => {
    try {
      const res = await api.get(`/driverexpense/${expenseId}/attachments`);
      const data = unwrapApiData(res);
      const list = Array.isArray(data) ? data : data?.data;
      setAttachments(Array.isArray(list) ? list : []);
    } catch (e) {
      setAttachments((prev) => prev || []);
    }
  };

  const uploadMoreAttachments = async (expenseId, files, desc) => {
    if (!files || files.length === 0) return;

    setUploadingMore(true);
    try {
      const fd = new FormData();

      if (desc) {
        fd.append("description", desc);
        fd.append("attachment_description", desc);
      }

      files.forEach((f) => fd.append("documents[]", f));

      await api.post(`/driverexpense/${expenseId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await refreshAttachments(expenseId);

      Swal.fire({
        icon: "success",
        title: "Uploaded",
        text: "Attachments uploaded successfully.",
      });
    } catch (err) {
      const payload = extractErrorPayload(err);
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text:
          payload?.data?.toString?.() ||
          payload?.message?.toString?.() ||
          "Unexpected error",
      });
    } finally {
      setUploadingMore(false);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      await api.delete(`/driverexpense-attachment/${attachmentId}`);
      if (createdExpense?.id) await refreshAttachments(createdExpense.id);
      Swal.fire({ icon: "success", title: "Deleted", text: "Attachment removed." });
    } catch (err) {
      const payload = extractErrorPayload(err);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: payload?.data?.toString?.() || payload?.message?.toString?.() || "Unexpected error",
      });
    }
  };

  const storageUrl = (path) => {
    if (!path) return null;
    const base = (process.env.REACT_APP_STORAGE_BASE_URL || "https://new.cajuu.app").replace(/\/+$/, "");
    const cleaned = String(path).replace(/^\/+/, "");
    return `${base}/storage/${cleaned}`;
  };

  const driverLabel = (d) => {
    const u = d?.user;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return `${full} (#${d.id})`;
    if (u?.email) return `${u.email} (#${d.id})`;
    return `Driver #${d.id}`;
  };

  const canShowAttachmentsPanel = Boolean(createdExpense?.id);

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <div className="flex items-center justify-between gap-3">
            <InfoSection title="NEW DRIVER EXPENSE" description="Create a new expense (with optional attachments)" />
          </div>
        </GroupedContainer>

        <form onSubmit={submit} className="space-y-8 mt-6">
          {/* ===== Expense Information ===== */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">EXPENSE INFORMATION</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Driver */}
                <div className="lg:col-span-2">
                  <label className="text-sm font-medium text-white mb-2 block">
                    Driver (required for Admin / optional for Driver)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbUser className="text-gray-400 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={driverId}
                      onChange={(e) => setDriverId(e.target.value)}
                      disabled={loadingRefs}
                    >
                      <option value="" className="bg-[#1c1b19]">
                        -- Select --
                      </option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id} className="bg-[#1c1b19]">
                          {driverLabel(d)}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>

                    {driverId && (
                      <button
                        type="button"
                        onClick={clearField(setDriverId)}
                        className="absolute inset-y-0 right-10 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="driver_id" />
                </div>

                {/* Category */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbFilter className="text-amber-500 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="Fuel" className="bg-[#1c1b19]">
                        Fuel
                      </option>
                      <option value="Storage Fee" className="bg-[#1c1b19]">
                        Storage Fee
                      </option>
                      <option value="Maintenance" className="bg-[#1c1b19]">
                        Maintenance
                      </option>
                      <option value="Supplies" className="bg-[#1c1b19]">
                        Supplies
                      </option>
                      <option value="Personal" className="bg-[#1c1b19]">
                        Personal
                      </option>
                      <option value="Owner Advance" className="bg-[#1c1b19]">
                        Owner Advance
                      </option>
                      <option value="Other" className="bg-[#1c1b19]">
                        Other
                      </option>
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>
                  </div>
                  <FieldError name="category" />
                </div>

                {/* Paid By (nullable) */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Paid By <span className="text-zinc-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbUser className="text-gray-400 text-lg" />
                    </div>

                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={paidBy}
                      onChange={(e) => setPaidBy(e.target.value)}
                    >
                      <option value="" className="bg-[#1c1b19]">
                        -- Not specified --
                      </option>
                      <option value="Driver" className="bg-[#1c1b19]">
                        Driver
                      </option>
                      <option value="Company" className="bg-[#1c1b19]">
                        Company
                      </option>
                    </select>

                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>

                    {paidBy && (
                      <button
                        type="button"
                        onClick={clearField(setPaidBy)}
                        className="absolute inset-y-0 right-10 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="paid_by" />
                </div>

                {/* ✅ Storage Fee-only fields */}
                {isStorageFee && (
                  <>
                    {/* Order ID */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Order ID <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbClipboardText className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={orderId}
                          onChange={(e) => setOrderId(e.target.value)}
                          placeholder="e.g. 12345"
                        />
                        {orderId && (
                          <button
                            type="button"
                            onClick={clearField(setOrderId)}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                            title="Clear"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name="order_id" />
                    </div>

                    {/* Buyer Number */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Buyer Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbClipboardText className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={buyerNumber}
                          onChange={(e) => setBuyerNumber(e.target.value)}
                          placeholder="e.g. B-001"
                        />
                        {buyerNumber && (
                          <button
                            type="button"
                            onClick={clearField(setBuyerNumber)}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                            title="Clear"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name="buyer_number" />
                    </div>

                    {/* Lot Number */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Lot Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbClipboardText className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={lotNumber}
                          onChange={(e) => setLotNumber(e.target.value)}
                          placeholder="e.g. 987654"
                        />
                        {lotNumber && (
                          <button
                            type="button"
                            onClick={clearField(setLotNumber)}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                            title="Clear"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name="lot_number" />
                    </div>
                  </>
                )}

                {/* Amount */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Amount ($) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbCurrencyDollar className="text-gray-400 text-lg" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      onBlur={handleAmountBlur}
                      placeholder="0.00"
                    />
                    {amount && (
                      <button
                        type="button"
                        onClick={clearField(setAmount)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="amount" />
                </div>

                {/* Description */}
                <div className="lg:col-span-3">
                  <label className="text-sm font-medium text-white mb-2 block">
                    Description{" "}
                    {category === "Other" ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="text-zinc-400">(optional)</span>
                    )}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbClipboardText className="text-gray-400 text-lg" />
                    </div>
                    <input
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={category === "Other" ? "Required for Other..." : "Optional..."}
                    />
                    {description && (
                      <button
                        type="button"
                        onClick={clearField(setDescription)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="description" />
                </div>

                {/* Expense Date/Time */}
                <div className="lg:col-span-1">
                  <label className="text-sm font-medium text-white mb-2 block">
                    Expense Date/Time <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbCalendar className="text-blue-500 text-lg" />
                    </div>
                    <input
                      type="datetime-local"
                      style={{ colorScheme: "dark" }}
                      className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={expenseAt}
                      onChange={(e) => setExpenseAt(e.target.value)}
                      onFocus={(e) => e.target.showPicker?.()}
                    />
                    {expenseAt && (
                      <button
                        type="button"
                        onClick={clearField(setExpenseAt)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="expense_at" />
                </div>
              </div>
            </div>
          </div>

          {/* ===== Attachments ===== */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Attachments</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Files (jpg, png, pdf) <span className="text-zinc-400">(optional)</span>
                  </label>

                  <div className="bg-[#292826] border border-zinc-600 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <TbPaperclip className="text-amber-500 text-xl" />
                      <div className="text-sm text-zinc-200">Attach now or after creating the expense.</div>
                    </div>

                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
                      onChange={(e) => setDocuments(Array.from(e.target.files || []))}
                    />

                    <div className="text-xs text-zinc-400 mt-2">Tip: receipts/invoices in PDF keep the best quality.</div>
                  </div>

                  <FieldError name="documents" />
                </div>

                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Attachment description <span className="text-zinc-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbClipboardText className="text-gray-400 text-lg" />
                    </div>
                    <input
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={attachmentDescription}
                      onChange={(e) => setAttachmentDescription(e.target.value)}
                      placeholder="Receipt / Invoice / etc."
                    />
                    {attachmentDescription && (
                      <button
                        type="button"
                        onClick={clearField(setAttachmentDescription)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        title="Clear"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Action Buttons ===== */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white font-medium"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors text-white font-medium disabled:opacity-60"
              disabled={submitting}
            >
              <Save size={18} />
              {submitting ? "Saving..." : "Save Expense"}
            </button>
          </div>
        </form>

        {/* ===== After create: manage attachments ===== */}
        {canShowAttachmentsPanel && (
          <div className="mt-10">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Expense Attachments</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-white">Manage attachments</div>
                  <div className="text-sm text-zinc-300">Expense #{createdExpense.id} • Upload more files anytime</div>
                </div>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white disabled:opacity-60"
                  onClick={() => refreshAttachments(createdExpense.id)}
                  disabled={uploadingMore}
                >
                  <TbRefresh className="text-lg" />
                  Refresh
                </button>
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <UploadMore
                  disabled={uploadingMore}
                  onUpload={(files, desc) => uploadMoreAttachments(createdExpense.id, files, desc)}
                />

                <div className="bg-[#292826] border border-zinc-600 rounded-xl p-5">
                  <div className="text-sm font-semibold mb-3 text-white">Existing attachments</div>

                  {attachments.length === 0 ? (
                    <div className="text-sm text-zinc-400">No attachments yet.</div>
                  ) : (
                    <div className="space-y-3">
                      {attachments.map((a) => (
                        <div key={a.id} className="border border-zinc-600 rounded-lg p-4 bg-zinc-950/30">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate text-white">
                                {a.original_name || a.path || `Attachment #${a.id}`}
                              </div>

                              <div className="text-xs text-zinc-400 mt-1">
                                {a.mime_type ? `${a.mime_type}` : ""}
                                {a.size ? ` • ${(Number(a.size) / 1024).toFixed(0)} KB` : ""}
                              </div>

                              {a.description ? <div className="text-xs text-zinc-300 mt-2">{a.description}</div> : null}

                              {a.path ? (
                                <a
                                  className="inline-flex items-center gap-2 text-xs mt-3 px-3 py-2 rounded-lg border border-zinc-600 hover:bg-zinc-900 transition-colors text-amber-400 hover:text-amber-300"
                                  href={storageUrl(a.path)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <TbPaperclip className="text-base" />
                                  Open file
                                </a>
                              ) : null}
                            </div>

                            <button
                              type="button"
                              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-zinc-600 hover:bg-zinc-900 transition-colors text-white"
                              onClick={() => deleteAttachment(a.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {loadingRefs && (
          <div className="fixed bottom-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-3 py-1 rounded text-xs shadow-lg">
            Loading drivers...
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

function UploadMore({ disabled, onUpload }) {
  const [files, setFiles] = useState([]);
  const [desc, setDesc] = useState("");

  const clear = (setter) => (e) => {
    e?.preventDefault?.();
    setter("");
  };

  return (
    <div className="bg-[#292826] border border-zinc-600 rounded-xl p-5">
      <div className="text-sm font-semibold mb-3 text-white">Upload more</div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-white mb-2 block">Files</label>
          <input
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.pdf"
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            disabled={disabled}
          />
          <div className="text-xs text-zinc-400 mt-2">Choose one or more files to attach.</div>
        </div>

        <div>
          <label className="text-sm font-medium text-white mb-2 block">Description (optional)</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <TbClipboardText className="text-gray-400 text-lg" />
            </div>
            <input
              className="w-full pl-10 pr-10 bg-zinc-900 placeholder:text-gray-500 py-3 text-sm border border-zinc-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Receipt photo / invoice / etc."
              disabled={disabled}
            />
            {desc && (
              <button
                type="button"
                onClick={clear(setDesc)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                title="Clear"
              >
                <TbX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors text-white font-medium disabled:opacity-60 w-full"
          disabled={disabled || files.length === 0}
          onClick={() => onUpload(files, desc)}
        >
          <Upload size={18} />
          {disabled ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>
  );
}

export default NewDriverExpense;
